import { useState, useEffect, useRef, useCallback } from 'react';

const useVideoPlayer = (videoElement: React.RefObject<HTMLVideoElement>) => {
  const [playerState, setPlayerState] = useState({
    isPlaying: false,
    progress: 0,
    speed: 1,
    isMuted: false,
    rotate: 0,
    isFullscreen: false,
  });
  const [controlsVisible, setControlsVisible] = useState(true);

  const controlsTimer = useRef<ReturnType<typeof setTimeout>>();
  const currentTimeRef = useRef('00:00');

  const showControls = useCallback(() => {
    setControlsVisible(true);
    clearTimeout(controlsTimer.current);
    controlsTimer.current = setTimeout(() => setControlsVisible(false), 3000);
  }, []);

  useEffect(() => {
    if (playerState.isPlaying) {
      showControls();
    }
  }, [playerState.isPlaying]);

  const togglePlay = () => {
    if (!videoElement.current) return;

    if (playerState.isPlaying) {
      videoElement.current.pause();
    } else {
      videoElement.current.play();
    }
  };

  const handlePlay = () => {
    setPlayerState((prev) => ({ ...prev, isPlaying: true }));
  };

  const handlePause = () => {
    setPlayerState((prev) => ({ ...prev, isPlaying: false }));
  };

  const handleOnTimeUpdate = () => {
    if (!videoElement.current) return;

    const { currentTime, duration } = videoElement.current;

    const minutes = Math.floor(currentTime / 60);
    const seconds = Math.floor(currentTime % 60);
    currentTimeRef.current = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    const progress = duration ? (currentTime / duration) * 100 : 0;

    setPlayerState((prev) => ({
      ...prev,
      progress,
    }));
  };

  const handleVideoEnded = () => {
    setPlayerState((prev) => ({ ...prev, isPlaying: false, progress: 100 }));
  };

  const handleVideoProgress = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!videoElement.current) return;
    const manualChange = Number(event.target.value);
    videoElement.current.currentTime =
      (videoElement.current.duration / 100) * manualChange;
    setPlayerState((prev) => ({ ...prev, progress: manualChange }));
  };

  const handleForward = () => {
    if (videoElement.current) {
      videoElement.current.currentTime += 10;
    }
  };

  const handleBackward = () => {
    if (videoElement.current) {
      videoElement.current.currentTime -= 10;
    }
  };

  const toggleFullScreen = () => {
    togglePlay();
    const videoUrl =
      videoElement.current?.currentSrc || videoElement.current?.src;
    if (!videoUrl) return;
    window.open(videoUrl, '_blank');
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setPlayerState((prev) => ({
        ...prev,
        isFullscreen: !!document.fullscreenElement,
      }));
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const rotateVideo = (value: number) => {
    setPlayerState((prev) => ({
      ...prev,
      rotate: prev.rotate + value,
    }));
  };

  useEffect(() => {
    if (videoElement.current) {
      videoElement.current.muted = playerState.isMuted;
    }
  }, [playerState.isMuted, videoElement]);

  return {
    playerState,
    togglePlay,
    handleOnTimeUpdate,
    handleVideoProgress,
    handleForward,
    handleBackward,
    toggleFullScreen,
    handlePlay,
    handlePause,
    handleVideoEnded,
    rotateVideo,
    currentTimeRef,
    controlsVisible,
    showControls,
  };
};

export default useVideoPlayer;
